import React, { useEffect, useRef, useState } from 'react';
import './Connect.css';
import { buildCameraPreviewDataUrl } from '../utils/cameraPreview';
import { parseWifiTelemetryPayload } from '../utils/wifiTelemetry';
import { queueDeviceOperation, startOperationSync } from '../utils/operationQueue';
import { upsertDeviceTelemetry } from '../utils/deviceTelemetry';

const APP_AUTH_TOKEN_KEY = 'auth_token';
const CONNECT_GUEST_TOKEN_KEY = 'connect_guest_token';
const CAMERA_URL_STORAGE_KEY = 'remu_bucket_camera_url_v1';
const WIFI_TELEMETRY_URL_STORAGE_KEY = 'remu_bucket_wifi_telemetry_url_v1';
const WIFI_SERIAL_NUMBER_STORAGE_KEY = 'remu_bucket_wifi_serial_number_v1';
const WIFI_SESSION_START_STORAGE_KEY = 'remu_bucket_wifi_session_start_v1';
const DEFAULT_CAMERA_URL = 'http://192.168.4.2:81/stream';
const DEFAULT_WIFI_TELEMETRY_URL = 'http://192.168.4.1/telemetry';

const ensureUrlScheme = (url) => (/^https?:\/\//i.test(url) ? url : `http://${url}`);

// iOS/WebKit cannot render multipart/x-mixed-replace, so this page falls back
// to polling the camera's own single-JPEG /view endpoint instead.
const deriveCameraViewUrl = (streamUrl) => {
  try {
    const parsed = new URL(ensureUrlScheme(streamUrl));
    parsed.port = '';
    parsed.pathname = '/view';
    parsed.search = '';
    return parsed.toString();
  } catch (error) {
    return streamUrl;
  }
};

function CameraStream() {
  const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '/api/v1';
  const getAuthToken = () => localStorage.getItem(APP_AUTH_TOKEN_KEY) || localStorage.getItem(CONNECT_GUEST_TOKEN_KEY);

  const [cameraUrl] = useState(() => localStorage.getItem(CAMERA_URL_STORAGE_KEY) || DEFAULT_CAMERA_URL);
  const [wifiTelemetryUrl] = useState(() => localStorage.getItem(WIFI_TELEMETRY_URL_STORAGE_KEY) || DEFAULT_WIFI_TELEMETRY_URL);
  const [serialNumber] = useState(() => localStorage.getItem(WIFI_SERIAL_NUMBER_STORAGE_KEY) || '');
  const [organizationCode] = useState(() => localStorage.getItem('organization_code') || '');
  const [customerId] = useState(() => Number(localStorage.getItem('customer_id') || 0));

  const [streamStatus, setStreamStatus] = useState('checking');
  const [useSnapshotFallback, setUseSnapshotFallback] = useState(false);
  const [snapshotAttempt, setSnapshotAttempt] = useState(0);
  const [wifiTelemetryStatus, setWifiTelemetryStatus] = useState('idle');
  const [telemetryText, setTelemetryText] = useState('');
  const [parsedTelemetry, setParsedTelemetry] = useState({ temperature: '', pressure: '' });
  const [connectionStatus, setConnectionStatus] = useState('Waiting for data...');
  const [readCount, setReadCount] = useState(0);
  const [recordingStatus, setRecordingStatus] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);

  const readCountRef = useRef(0);
  const parsedTelemetryRef = useRef({ temperature: '', pressure: '' });
  const telemetryTextRef = useRef('');
  const connectionStatusRef = useRef('Waiting for data...');
  const isRecordingRef = useRef(false);
  const lastRecordingUrlRef = useRef('');

  useEffect(() => startOperationSync(), []);

  const finishSession = () => {
    const sessionStart = localStorage.getItem(WIFI_SESSION_START_STORAGE_KEY);

    if (serialNumber && customerId) {
      queueDeviceOperation({
        serial_number: serialNumber,
        customer_id: customerId,
        organization_code: organizationCode,
        operation_start: sessionStart || new Date().toISOString(),
        operation_end: new Date().toISOString(),
        notes: telemetryTextRef.current,
        temperature_summary: parsedTelemetryRef.current.temperature || '',
        pressure_summary: parsedTelemetryRef.current.pressure || '',
        camera_snapshot_data: '',
        recording_url: lastRecordingUrlRef.current || '',
        source: 'wifi'
      });
    }

    localStorage.removeItem(WIFI_SESSION_START_STORAGE_KEY);
    setSessionEnded(true);
  };

  const publishTelemetry = async ({ status, telemetry, temperature, pressure, cameraSnapshotData, recordingUrl, count }) => {
    if (!serialNumber) {
      return;
    }

    const updatedAt = new Date().toISOString();

    upsertDeviceTelemetry({
      serialNumber,
      deviceName: serialNumber,
      customerId,
      organizationCode,
      connected: true,
      status,
      telemetry,
      cameraUrl: ensureUrlScheme(cameraUrl),
      cameraSnapshotData: cameraSnapshotData || '',
      recordingUrl: recordingUrl || '',
      parsedTelemetry: { temperature, pressure },
      readCount: count,
      updatedAt
    });

    const token = getAuthToken();
    if (!token) {
      return;
    }

    try {
      await fetch(`${getApiBaseUrl()}/telemetry/live`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          serial_number: serialNumber,
          customer_id: customerId,
          organization_code: organizationCode,
          device_name: serialNumber,
          connected: true,
          status,
          telemetry,
          temperature,
          pressure,
          camera_url: ensureUrlScheme(cameraUrl),
          camera_snapshot_data: cameraSnapshotData || '',
          recording_url: recordingUrl || '',
          read_count: count,
          updated_at: updatedAt
        })
      });
    } catch (error) {
      // Best-effort relay; local telemetry cache still updates the UI.
    }
  };

  const fetchTelemetry = async () => {
    if (!wifiTelemetryUrl) {
      return;
    }

    try {
      const response = await fetch(wifiTelemetryUrl, { cache: 'no-store' });
      if (!response.ok) {
        setWifiTelemetryStatus('unavailable');
        return;
      }

      const payloadText = await response.text();
      let parsed = null;

      try {
        parsed = parseWifiTelemetryPayload(JSON.parse(payloadText));
      } catch (error) {
        const fallbackText = payloadText.trim();
        if (fallbackText) {
          parsed = { temperature: '', pressure: '', telemetry: fallbackText, status: 'Connected via Wi‑Fi' };
        }
      }

      if (!parsed) {
        setWifiTelemetryStatus('idle');
        return;
      }

      const nextReadCount = readCountRef.current + 1;
      readCountRef.current = nextReadCount;
      parsedTelemetryRef.current = { temperature: parsed.temperature || '', pressure: parsed.pressure || '' };
      telemetryTextRef.current = parsed.telemetry || '';
      connectionStatusRef.current = parsed.status || 'Connected via Wi‑Fi';

      setWifiTelemetryStatus('available');
      setTelemetryText(parsed.telemetry || '');
      setParsedTelemetry(parsedTelemetryRef.current);
      setConnectionStatus(connectionStatusRef.current);
      setReadCount(nextReadCount);

      void publishTelemetry({
        status: connectionStatusRef.current,
        telemetry: telemetryTextRef.current,
        temperature: parsedTelemetryRef.current.temperature,
        pressure: parsedTelemetryRef.current.pressure,
        count: nextReadCount
      });
    } catch (error) {
      setWifiTelemetryStatus('unavailable');
    }
  };

  useEffect(() => {
    setWifiTelemetryStatus('checking');
    fetchTelemetry();
    const timer = setInterval(() => {
      void fetchTelemetry();
    }, 3000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wifiTelemetryUrl]);

  useEffect(() => {
    if (!useSnapshotFallback) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSnapshotAttempt((count) => count + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [useSnapshotFallback]);

  const currentImageSrc = useSnapshotFallback
    ? `${deriveCameraViewUrl(cameraUrl)}?t=${snapshotAttempt}`
    : ensureUrlScheme(cameraUrl);

  const captureSnapshotOverlay = async () => {
    const streamImage = document.querySelector('.camera-stream-page-image');
    if (!streamImage || !streamImage.complete || !streamImage.naturalWidth) {
      return;
    }

    const snapshot = await buildCameraPreviewDataUrl({
      image: streamImage,
      temperature: parsedTelemetryRef.current.temperature || '--',
      pressure: parsedTelemetryRef.current.pressure || '--',
      status: connectionStatusRef.current || 'idle',
      serialNumber
    });

    if (snapshot) {
      void publishTelemetry({
        status: connectionStatusRef.current,
        telemetry: telemetryTextRef.current,
        temperature: parsedTelemetryRef.current.temperature,
        pressure: parsedTelemetryRef.current.pressure,
        cameraSnapshotData: snapshot,
        count: readCountRef.current
      });
    }
  };

  const handleRecordClip = async () => {
    if (isRecordingRef.current) {
      return;
    }

    const streamImage = document.querySelector('.camera-stream-page-image');
    if (!streamImage || !streamImage.complete || !streamImage.naturalWidth) {
      setRecordingStatus('Waiting for a valid camera frame before recording');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const context = canvas.getContext('2d');
    if (!context) {
      setRecordingStatus('Canvas capture is not available in this browser');
      return;
    }

    const stream = canvas.captureStream?.(30);
    if (!stream) {
      setRecordingStatus('This browser does not support recording canvas video');
      return;
    }

    const chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    isRecordingRef.current = true;
    setIsRecording(true);
    setRecordingStatus('Recording 20s clip...');

    const drawTicker = setInterval(() => {
      const currentImage = document.querySelector('.camera-stream-page-image');
      if (!currentImage) {
        return;
      }

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(currentImage, 0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(8, 15, 24, 0.65)';
      context.fillRect(16, 16, 310, 120);
      context.font = 'bold 20px sans-serif';
      context.fillStyle = '#ffffff';
      const lines = [
        serialNumber ? `Device: ${serialNumber}` : 'Device: unknown',
        `Temp: ${parsedTelemetryRef.current.temperature || '--'}`,
        `Pressure: ${parsedTelemetryRef.current.pressure || '--'}`,
        `Status: ${connectionStatusRef.current || 'idle'}`
      ];
      lines.forEach((line, index) => {
        context.fillText(line, 28, 42 + index * 24);
      });
    }, 250);

    recorder.start();

    setTimeout(() => {
      clearInterval(drawTicker);
      recorder.stop();
    }, 20000);

    recorder.onstop = async () => {
      const clipBlob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
      const formData = new FormData();
      formData.append('file', clipBlob, `${serialNumber || 'device'}-clip.webm`);
      formData.append('serial_number', serialNumber || '');
      formData.append('customer_id', String(customerId || 0));
      formData.append('organization_code', organizationCode || '');
      formData.append('device_name', serialNumber || '');
      formData.append('status', 'Recorded 20s clip');
      formData.append('telemetry', telemetryTextRef.current || '');
      formData.append('temperature', parsedTelemetryRef.current.temperature || '');
      formData.append('pressure', parsedTelemetryRef.current.pressure || '');

      try {
        const token = getAuthToken();
        const uploadResponse = await fetch(`${getApiBaseUrl()}/telemetry/clip`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        const uploadData = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || 'Failed to upload recording');
        }

        setRecordingStatus('Clip uploaded successfully');
        lastRecordingUrlRef.current = uploadData.recording_url || '';
        void publishTelemetry({
          status: 'Recorded 20s clip',
          telemetry: telemetryTextRef.current,
          temperature: parsedTelemetryRef.current.temperature,
          pressure: parsedTelemetryRef.current.pressure,
          recordingUrl: uploadData.recording_url || '',
          count: readCountRef.current
        });
      } catch (error) {
        setRecordingStatus(error.message || 'Recording upload failed');
      } finally {
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    };
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Camera & Live Data</h1>
        <p>
          {serialNumber
            ? `Device: ${serialNumber}`
            : 'No device selected. Return to Connect and start a Wi‑Fi connection first.'}
        </p>
      </div>

      <div className="content-grid">
        <div className="card">
          <div className={`status-panel status-${wifiTelemetryStatus === 'available' ? 'success' : wifiTelemetryStatus === 'unavailable' ? 'error' : 'neutral'}`}>
            {wifiTelemetryStatus === 'checking' && 'Checking local Wi‑Fi telemetry...'}
            {wifiTelemetryStatus === 'available' && 'Local Wi‑Fi telemetry is live'}
            {wifiTelemetryStatus === 'unavailable' && 'Unable to reach the local Wi‑Fi telemetry endpoint'}
            {wifiTelemetryStatus === 'idle' && 'Local Wi‑Fi telemetry is waiting for a response'}
          </div>

          <img
            className="camera-preview camera-stream-page-image"
            src={currentImageSrc}
            alt="ESP32 camera preview"
            crossOrigin="anonymous"
            onLoad={async () => {
              setStreamStatus('available');
              await captureSnapshotOverlay();
            }}
            onError={() => {
              setStreamStatus('unavailable');
              setUseSnapshotFallback(true);
            }}
          />

          <div className={`status-panel status-${streamStatus === 'available' ? 'success' : 'error'} camera-stream-status`}>
            {streamStatus === 'available'
              ? (useSnapshotFallback ? 'Showing refreshed snapshots (live MJPEG unsupported in this browser)' : 'Stream is live')
              : 'Waiting for a valid camera frame...'}
          </div>

          <button type="button" onClick={handleRecordClip} disabled={isRecording} className="btn btn-primary camera-record-button">
            {isRecording ? 'Recording 20s...' : 'Record 20s Clip'}
          </button>

          <button
            type="button"
            onClick={finishSession}
            disabled={sessionEnded || !serialNumber}
            className="btn btn-secondary"
          >
            {sessionEnded ? 'Session sent to database' : 'Finish Session & Send to Database'}
          </button>

          {recordingStatus && <div className="message success">{recordingStatus}</div>}

          <div className="telemetry-box">
            <div className="telemetry-label">Live data</div>
            <div className="live-metrics">
              <div className="live-metric">
                <span className="metric-name">Temperature</span>
                <span className="metric-value">{parsedTelemetry.temperature || '--'}</span>
              </div>
              <div className="live-metric">
                <span className="metric-name">Pressure</span>
                <span className="metric-value">{parsedTelemetry.pressure || '--'}</span>
              </div>
            </div>
            <pre>{telemetryText || 'Waiting for telemetry payload...'}</pre>
            <div className="telemetry-meta">Notifications received: {readCount}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CameraStream;
